/**
 * Design Tokens — Single source of truth for Grufity's design system.
 * Inspired by Apple/Google design principles: restrained, clean, trustworthy.
 */

// ─── Color Palette ──────────────────────────────────────────────────────────────

export const colors = {
  // Primary brand color — deep navy, conveys trust and professionalism
  primary: {
    main: '#1a2b4a',
    light: '#2d4a7a',
    dark: '#0f1a2e',
    50: '#f0f4fa',
    100: '#dce4f2',
    200: '#b8c9e5',
    300: '#8aa6d0',
    400: '#5c83bb',
    500: '#1a2b4a',
    600: '#162440',
    700: '#121d35',
    800: '#0e162a',
    900: '#0a0f1f',
    contrastText: '#ffffff',
  },

  // Accent — calm, professional blue for interactive elements
  accent: {
    main: '#2563eb',
    light: '#5b8def',
    dark: '#1a4fd4',
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#2563eb',
    600: '#1d4ed8',
    contrastText: '#ffffff',
  },

  // Neutral grays — refined scale for text, backgrounds, borders
  neutral: {
    0: '#ffffff',
    25: '#fcfcfd',
    50: '#f8f9fb',
    100: '#f1f3f6',
    200: '#e5e8ed',
    300: '#d1d5dc',
    400: '#9ca3ae',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },

  // Semantic colors
  success: {
    main: '#16a34a',
    light: '#22c55e',
    dark: '#15803d',
    bg: '#f0fdf4',
    contrastText: '#ffffff',
  },
  error: {
    main: '#dc2626',
    light: '#ef4444',
    dark: '#b91c1c',
    bg: '#fef2f2',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#d97706',
    light: '#f59e0b',
    dark: '#b45309',
    bg: '#fffbeb',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0284c7',
    light: '#0ea5e9',
    dark: '#0369a1',
    bg: '#f0f9ff',
    contrastText: '#ffffff',
  },

  // Positive/negative for financial data
  positive: '#16a34a',
  negative: '#dc2626',
};

// ─── Typography ────────────────────────────────────────────────────────────────

export const fontFamily = [
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
].join(',');

export const typography = {
  fontFamily,
  h1: {
    fontWeight: 700,
    fontSize: 'clamp(2rem, 1.4rem + 1.6vw, 3rem)',
    lineHeight: 1.15,
    letterSpacing: '-0.025em',
    color: colors.neutral[900],
  },
  h2: {
    fontWeight: 700,
    fontSize: 'clamp(1.5rem, 1.1rem + 1.1vw, 2.25rem)',
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: colors.neutral[900],
  },
  h3: {
    fontWeight: 600,
    fontSize: 'clamp(1.25rem, 1rem + 0.7vw, 1.75rem)',
    lineHeight: 1.3,
    letterSpacing: '-0.015em',
    color: colors.neutral[900],
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
    color: colors.neutral[900],
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.1rem',
    lineHeight: 1.4,
    color: colors.neutral[800],
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.5,
    color: colors.neutral[800],
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
    color: colors.neutral[700],
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: colors.neutral[600],
  },
  caption: {
    fontSize: '0.8rem',
    lineHeight: 1.5,
    color: colors.neutral[500],
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    lineHeight: 1.5,
    color: colors.accent.main,
  },
  subtitle1: {
    fontSize: '1.05rem',
    lineHeight: 1.6,
    color: colors.neutral[600],
  },
  subtitle2: {
    fontSize: '0.9rem',
    fontWeight: 500,
    lineHeight: 1.5,
    color: colors.neutral[600],
  },
};

// ─── Spacing ───────────────────────────────────────────────────────────────────

export const spacing = 8; // MUI default, 1 unit = 8px

// ─── Border Radii ──────────────────────────────────────────────────────────────

export const radii = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  pill: 999,
};

// ─── Shadows ───────────────────────────────────────────────────────────────────

export const shadows = {
  none: 'none',
  xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.04)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.06), 0 4px 6px -2px rgba(0, 0, 0, 0.03)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.06), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
  card: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
  cardHover: '0 8px 25px -5px rgba(0, 0, 0, 0.08), 0 4px 10px -3px rgba(0, 0, 0, 0.03)',
  input: '0 1px 2px rgba(0, 0, 0, 0.05)',
  inputFocus: '0 0 0 3px rgba(37, 99, 235, 0.15)',
};

// ─── Chart Colors ──────────────────────────────────────────────────────────────
// Per-model series colors for performance charts

export const chart = {
  series: {
    // Stock models
    LARGECAP: { main: '#4f63d2', light: '#7b8ae8', gradient: ['#4f63d2', '#6c7fe0', '#4f63d2'] },
    MIDCAP:   { main: '#7c3aed', light: '#a78bfa', gradient: ['#7c3aed', '#9f67f8', '#7c3aed'] },
    SMALLCAP: { main: '#0891b2', light: '#22d3ee', gradient: ['#0891b2', '#22d3ee', '#0891b2'] },
    // Fund models
    QQQ: { main: '#059669', light: '#34d399', gradient: ['#059669', '#34d399', '#059669'] },
    SPY: { main: '#2563eb', light: '#60a5fa', gradient: ['#2563eb', '#60a5fa', '#2563eb'] },
  },
  benchmark:  { main: '#94a3b8', light: '#cbd5e1' },
  grid:       '#f1f3f6',
  axis:       '#e5e8ed',
  axisLabel:  '#6b7280',
  area: {
    topOpacity: 0.18,
    midOpacity: 0.06,
    bottomOpacity: 0.01,
  },
};

// ─── Card Tokens ───────────────────────────────────────────────────────────────

export const card = {
  bg: '#ffffff',
  border: colors.neutral[200],
  borderHover: colors.neutral[300],
  radius: 16,
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
  shadowHover: '0 8px 24px -4px rgba(0,0,0,0.08), 0 4px 8px -2px rgba(0,0,0,0.03)',
  // Premium elevated card (e.g. comparison panels)
  premium: {
    border: colors.neutral[200],
    shadow: '0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    shadowHover: '0 12px 32px -6px rgba(0,0,0,0.1), 0 6px 12px -4px rgba(0,0,0,0.04)',
  },
  sectionBg: colors.neutral[50],
};

// ─── Pricing Tokens ────────────────────────────────────────────────────────────

export const pricing = {
  highlight: colors.accent[50],
  highlightBorder: colors.accent.main,
  badge: { bg: colors.accent.main, color: '#ffffff' },
  cta: { bg: colors.accent.main, hoverBg: colors.accent.dark, color: '#ffffff' },
  toggle: { bg: colors.neutral[100], selectedBg: '#ffffff', selectedShadow: '0 1px 3px rgba(0,0,0,0.1)' },
};

// ─── Semantic Performance Colors ───────────────────────────────────────────────

export const performance = {
  positive: { text: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  negative: { text: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  neutral:  { text: colors.neutral[600], bg: colors.neutral[50], border: colors.neutral[200] },
};

// ─── Transitions ───────────────────────────────────────────────────────────────

export const transitions = {
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
};
